import { isCancelledError } from "@tanstack/react-query";
import { classifyRequestFailure, defineEvent } from "@diverge/observability/browser";
import { dashboardObservability } from "./observability";

/**
 * Shared classification of failed dashboard requests. The query client's retry
 * policy, every polled query/interval and their failure reporting use it, so a
 * permission denial or an expired session never turns into a retry storm or a
 * stream of error reports.
 */

/** Minimal query shape read by refetchInterval callbacks (avoids Query generic variance). */
export type PolledQuery = {
  state: {
    status: string;
    error: unknown;
  };
};

/** What a failed dashboard request was for: the breadcrumb's and the degradation's closed reasons. */
const REQUEST_FAILURE_REASONS = [
  "agent_bundle_progress_unavailable",
  "benchmark_runs_unavailable",
  "chatbot_widget_config_unavailable",
  "conversation_detail_sync_failed",
  "conversation_detail_unavailable",
  "conversation_update_job_progress_unavailable",
  "critical_bug_count_unavailable",
  "dev_board_mention_count_unavailable",
  "leads_count_unavailable",
  "livechat_notification_count_unavailable",
  "livechat_presence_heartbeat_failed",
  "organization_capability_check_failed",
  "pending_agent_proposals_count_unavailable",
  "purchase_conversations_unavailable",
  "report_counts_unavailable",
  "settings_save_refused",
  "statistics_unavailable",
  "unread_comments_count_unavailable",
] as const;
export type RequestFailureReason = (typeof REQUEST_FAILURE_REASONS)[number];

const requestDegraded = defineEvent({
  name: "dashboard.request.degraded",
  message: "Dashboard request did not complete",
  level: "warn",
  classification: "operational",
  volume: "boundary",
  owner: "dashboard/requests",
  attributes: { "di.reason_code": { type: "category", values: REQUEST_FAILURE_REASONS } },
});

/** HTTP status of an axios-style error, or undefined when no response arrived. */
export function getHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const status = (error as { response?: { status?: unknown } }).response?.status;
  return typeof status === "number" ? status : undefined;
}

/** Auth/permission denials and missing resources: retrying or polling will not heal them. */
export function isHardRequestFailure(error: unknown): boolean {
  const status = getHttpStatus(error);
  return status === 401 || status === 403 || status === 404;
}

/**
 * The request never got an HTTP answer: offline, timeout, CORS or cancellation,
 * including TanStack's cancellation of a fetch a query invalidation replaced.
 */
export function isNetworkRequestFailure(error: unknown): boolean {
  if (!error || typeof error !== "object" || getHttpStatus(error) !== undefined) return false;
  const failure = classifyRequestFailure(error);
  return (
    failure === "cancelled" ||
    failure === "unavailable" ||
    isCancelledError(error) ||
    (error as { isAxiosError?: unknown }).isAxiosError === true
  );
}

/**
 * Failures that happen in normal operation and are not incidents: an expired
 * session, a permission denial, or no connectivity.
 */
export function isExpectedRequestFailure(error: unknown): boolean {
  const status = getHttpStatus(error);
  return status === 401 || status === 403 || isNetworkRequestFailure(error);
}

/** Whether a failed request may heal on retry: network errors, 5xx, 408 and 429. */
export function isTransientRequestFailure(error: unknown): boolean {
  const status = getHttpStatus(error);
  if (status === undefined) return true;
  return status === 408 || status === 429 || status >= 500;
}

/**
 * A TanStack `retry` callback that retries transient failures up to
 * `maxRetries` times and never retries another 4xx.
 */
export function retryUpTo(maxRetries: number) {
  return (failureCount: number, error: unknown): boolean =>
    failureCount < maxRetries && isTransientRequestFailure(error);
}

/**
 * A `refetchInterval` result that stops polling after a hard failure. A later
 * refetch that succeeds (mount, window focus, invalidation) resumes it.
 */
export function pollingRefetchInterval(
  query: PolledQuery,
  intervalMs: number | false,
): number | false {
  if (intervalMs === false) return false;
  if (query.state.status === "error" && isHardRequestFailure(query.state.error)) return false;
  return intervalMs;
}

export interface ReportRequestFailureOptions {
  /** A 4xx is the server refusing this input (validation, a lock, a conflict), which the caller shows. */
  rejectionExpected?: boolean;
}

/**
 * Reports a failure that its owner handles. Expected failures (cancelled,
 * offline, an expired session, a denial and, when the owner says so, a
 * refusal) only leave a breadcrumb. A 5xx is the API's own recorded failure,
 * so it is a dashboard.request.degraded warning rather than a second incident.
 * Anything else goes to `report`, the owner's logged event.
 */
export function reportRequestFailure(
  error: unknown,
  reason: RequestFailureReason,
  report?: (error: unknown) => void,
  options: ReportRequestFailureOptions = {},
): void {
  const status = getHttpStatus(error);
  const refused =
    options.rejectionExpected && status !== undefined && status >= 400 && status < 500;
  if (isExpectedRequestFailure(error) || refused || !report) {
    dashboardObservability.addBreadcrumb("dependency.failed", {
      operation: "dashboard.polling",
      reason,
      status: status ?? 0,
    });
    return;
  }
  if (status !== undefined && status >= 500) {
    dashboardObservability
      .logger("dashboard/requests")
      .error(requestDegraded, error, { "di.reason_code": reason });
    return;
  }
  report(error);
}

export interface PollingFailureEpisode {
  /** Records a failed tick and returns whether polling should stop. */
  fail(error: unknown): boolean;
  /** Ends the current failure episode. */
  succeed(): void;
}

/**
 * Tracks one poller's failure episode: only the first failure after a success
 * is reported, so a poll that keeps failing produces one record, not one per tick.
 */
export function createPollingFailureEpisode(
  reason: RequestFailureReason,
  report?: (error: unknown) => void,
): PollingFailureEpisode {
  let failing = false;
  return {
    fail(error) {
      if (!failing) {
        failing = true;
        reportRequestFailure(error, reason, report);
      }
      return isHardRequestFailure(error);
    },
    succeed() {
      failing = false;
    },
  };
}
